import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OrderForm.css';
import { 
  ArrowLeftIcon, 
  TimerIcon, 
  CopyIcon, 
  CheckIcon
} from './Icons';

const OrderForm = ({ selectedCrypto, onBack }) => {
  const [step, setStep] = useState(1); // 1: Details, 2: Payment Method Selection, 3: Payment, 4: Processing, 5: Success
  const [orderId, setOrderId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [newOrderLoading, setNewOrderLoading] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(94); // Default fallback rate
  const [rateLoading, setRateLoading] = useState(false);
  const [formData, setFormData] = useState({
    flashAmount: '',
    walletAddress: '',
    name: '',
    email: '',
    telegramContact: '',
    couponCode: '',
    transactionHash: '',
    screenshot: null
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [paymentData, setPaymentData] = useState({
    qrCode: '',
    paymentWallet: '',
    amountToPay: 0
  });
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes in seconds
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [discount, setDiscount] = useState(0);

  // Fetch live exchange rate
  useEffect(() => {
    const fetchExchangeRate = async () => {
      setRateLoading(true);
      try {
        // Using CoinGecko API for USDT to INR rate (free, no API key needed)
        const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=tether&vs_currencies=inr');
        const rate = response.data.tether.inr;
        setExchangeRate(rate);
        console.log('Live exchange rate fetched:', rate, 'INR/USDT');
      } catch (error) {
        console.error('Failed to fetch exchange rate, using default:', error);
        // Keep default rate of 94 if API fails
      } finally {
        setRateLoading(false);
      }
    };

    fetchExchangeRate();
    
    // Refresh rate every 5 minutes
    const interval = setInterval(fetchExchangeRate, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Generate Order ID
  useEffect(() => {
    if (!orderId) {
      const newOrderId = 'FU' + Date.now() + Math.floor(Math.random() * 1000);
      setOrderId(newOrderId);
    }
  }, [orderId]);

  // Countdown Timer
  useEffect(() => {
    if (step === 3 && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, screenshot: file }));
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, screenshot: file }));
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeScreenshot = () => {
    setFormData(prev => ({ ...prev, screenshot: null }));
    setScreenshotPreview(null);
  };

  const applyCoupon = () => {
    if (formData.couponCode.toUpperCase() === 'FLASH10') {
      setDiscount(10);
      setMessage({ type: 'success', text: '10% discount applied!' });
    } else if (formData.couponCode.toUpperCase() === 'FLASH20') {
      setDiscount(20);
      setMessage({ type: 'success', text: '20% discount applied!' });
    } else {
      setDiscount(0);
      setMessage({ type: 'error', text: 'Invalid coupon code' });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const calculateTotal = () => {
    const flashAmount = parseFloat(formData.flashAmount) || 0;
    const usdtAmount = flashAmount / 50; // 1 USDT = 50 Flash USDT
    const discountAmount = (usdtAmount * discount) / 100;
    return (usdtAmount - discountAmount).toFixed(2);
  };

  const proceedToPayment = () => {
    const flashAmount = parseFloat(formData.flashAmount);
    if (flashAmount < 500) {
      setMessage({ type: 'error', text: 'Minimum order is 500 Flash USDT (10 USDT)' });
      return;
    }
    if (!formData.walletAddress || !formData.name || !formData.email) {
      setMessage({ type: 'error', text: 'Please fill all required fields' });
      return;
    }

    // Show loading state
    setCheckoutLoading(true);
    setMessage({ type: '', text: '' });

    // Simulate processing delay
    setTimeout(() => {
      setStep(2); // Go to payment method selection
      setCheckoutLoading(false);
    }, 1000);
  };

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    setMessage({ type: '', text: '' });

    // If UPI selected, no need to show payment details
    if (method === 'UPI') {
      setStep(3);
      return;
    }

    // For crypto payments, set wallet and QR code
    const wallets = {
      'Bitcoin': 'bc1q8sqc7x0wtdaz3c5fqcudcrz9wwf3473h22538z',
      'SOL USDT': 'F96HS7BJ87to7pcmTDegrBzJsCJQA43uNbBjEaWtziGT',
      'BEP20 USDT': '0x4BD2c994ADdb395eaca691bBE63708B5ACdEDe96',
      'TRC20 USDT': 'TUS9fVER1b4Gt24QeRpkGPSG2Z5PtddonS'
    };

    const qrCodes = {
      'Bitcoin': '/BTCcoinQR.jpg',
      'SOL USDT': '/SOLUSDTQR.jpg',
      'BEP20 USDT': '/BEP20USDTQR.jpg',
      'TRC20 USDT': '/TRC20USDTQR.jpg'
    };

    setPaymentData({
      qrCode: qrCodes[method] || '/qr-code-placeholder.png',
      paymentWallet: wallets[method] || 'YOUR_WALLET_ADDRESS',
      amountToPay: calculateTotal()
    });

    setStep(3);
    setTimeLeft(1800); // Reset timer
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Show processing screen
    setStep(4);
    setProcessing(true);
    setProcessingProgress(0);

    // Simulate processing with progress
    const progressInterval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return 95;
        }
        return prev + 5;
      });
    }, 200);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('orderId', orderId);
      formDataToSend.append('crypto', selectedCrypto.name);
      formDataToSend.append('paymentMethod', selectedPaymentMethod);
      formDataToSend.append('flashAmount', formData.flashAmount);
      formDataToSend.append('usdtAmount', calculateTotal());
      formDataToSend.append('walletAddress', formData.walletAddress);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telegramContact', formData.telegramContact);
      formDataToSend.append('couponCode', formData.couponCode);
      formDataToSend.append('discount', discount);
      formDataToSend.append('transactionHash', formData.transactionHash);
      if (formData.screenshot) {
        formDataToSend.append('screenshot', formData.screenshot);
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/submit-order`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        // Complete progress
        clearInterval(progressInterval);
        setProcessingProgress(100);
        
        // Wait a moment before showing success
        setTimeout(() => {
          setProcessing(false);
          setStep(5);
        }, 800);
      }
    } catch (error) {
      clearInterval(progressInterval);
      setProcessing(false);
      setStep(3); // Go back to payment step
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Failed to submit order. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="order-form-section">
      <div className="container">
        {step !== 4 && step !== 5 && (
          <button className="back-btn" onClick={() => {
            if (step === 2) {
              // Go back to order details
              setStep(1);
            } else if (step === 3) {
              // Go back to payment method selection
              setStep(2);
              setSelectedPaymentMethod(null);
            } else {
              // Go back to crypto selection
              onBack();
            }
          }}>
            <ArrowLeftIcon size={20} />
          </button>
        )}
        
        {/* STEP 1: Order Details */}
        {step === 1 && (
          <div className="order-container">
            <div className="order-form-card">
              <div className="selected-crypto-header">
                <img src={selectedCrypto.icon} alt={selectedCrypto.name} />
                <h2>{selectedCrypto.name}</h2>
                <p className="order-id">Order ID: {orderId}</p>
              </div>

              <div className="form-group">
                <label>Flash USDT Amount *</label>
                <input
                  type="number"
                  name="flashAmount"
                  value={formData.flashAmount}
                  onChange={handleChange}
                  placeholder="Minimum 500 Flash USDT"
                  required
                  min="500"
                />
                <small>1 USDT = 50 Flash USDT</small>
              </div>

              <div className="form-group">
                <label>Your Wallet Address (to receive Flash USDT) *</label>
                <input
                  type="text"
                  name="walletAddress"
                  value={formData.walletAddress}
                  onChange={handleChange}
                  placeholder="Enter your wallet address"
                  required
                />
              </div>

              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Telegram Contact (Optional)</label>
                <input
                  type="text"
                  name="telegramContact"
                  value={formData.telegramContact}
                  onChange={handleChange}
                  placeholder="@username or phone number"
                />
                <small>For faster order updates and support</small>
              </div>
            </div>

            <div className="order-summary-card">
              <h3>Order Summary</h3>
              <div className="summary-row">
                <span>Flash USDT Amount:</span>
                <span>{formData.flashAmount || 0} FUSDT</span>
              </div>
              <div className="summary-row">
                <span>USDT to Pay:</span>
                <span>{(parseFloat(formData.flashAmount) / 50 || 0).toFixed(2)} USDT</span>
              </div>
              
              <div className="coupon-section">
                <input
                  type="text"
                  name="couponCode"
                  value={formData.couponCode}
                  onChange={handleChange}
                  placeholder="Enter coupon code"
                  className="coupon-input"
                />
                <button type="button" onClick={applyCoupon} className="apply-btn">
                  Apply
                </button>
              </div>

              {discount > 0 && (
                <div className="summary-row discount">
                  <span>Discount ({discount}%):</span>
                  <span>-{((parseFloat(formData.flashAmount) / 50) * discount / 100).toFixed(2)} USDT</span>
                </div>
              )}

              <div className="summary-row total">
                <span>Total Amount:</span>
                <span>{calculateTotal()} USDT</span>
              </div>

              {message.text && (
                <div className={`message ${message.type}`}>
                  {message.text}
                </div>
              )}

              <button 
                type="button" 
                onClick={proceedToPayment} 
                className="checkout-btn"
                disabled={checkoutLoading}
              >
                {checkoutLoading ? (
                  <>
                    <span className="button-spinner"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Proceed to Checkout
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Payment Method Selection */}
        {step === 2 && (
          <div className="payment-method-container">
            <div className="payment-method-card">
              <h2>Select Payment Method</h2>
              <p className="payment-subtitle">Choose how you want to pay for your Flash USDT</p>

              <div className="payment-methods-grid">
                {/* Crypto Payment Options */}
                <button 
                  className="payment-method-option"
                  onClick={() => handlePaymentMethodSelect('BEP20 USDT')}
                >
                  <div className="method-icon">
                    <img src="/Binancebep20.webp" alt="BEP20 USDT" />
                  </div>
                  <div className="method-info">
                    <h3>BEP20 USDT</h3>
                    <p>Binance Smart Chain</p>
                    <span className="method-badge">Recommended</span>
                  </div>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>

                <button 
                  className="payment-method-option"
                  onClick={() => handlePaymentMethodSelect('TRC20 USDT')}
                >
                  <div className="method-icon">
                    <img src="/USDTtron.png" alt="TRC20 USDT" />
                  </div>
                  <div className="method-info">
                    <h3>TRC20 USDT</h3>
                    <p>Tron Network</p>
                  </div>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>

                <button 
                  className="payment-method-option"
                  onClick={() => handlePaymentMethodSelect('SOL USDT')}
                >
                  <div className="method-icon">
                    <img src="/Solana_logo.png" alt="SOL USDT" />
                  </div>
                  <div className="method-info">
                    <h3>SOL USDT</h3>
                    <p>Solana Network</p>
                  </div>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>

                <button 
                  className="payment-method-option"
                  onClick={() => handlePaymentMethodSelect('Bitcoin')}
                >
                  <div className="method-icon">
                    <img src="/Bitcoin-Logo.png" alt="Bitcoin" />
                  </div>
                  <div className="method-info">
                    <h3>Bitcoin</h3>
                    <p>BTC Network</p>
                    <span className="method-badge" style={{background: 'linear-gradient(135deg, #F7931A, #FF6B00)'}}>Popular</span>
                  </div>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>

                {/* UPI Payment Option */}
                <button 
                  className="payment-method-option upi-option"
                  onClick={() => handlePaymentMethodSelect('UPI')}
                >
                  <div className="method-icon upi-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                      <rect x="2" y="5" width="20" height="14" rx="2" stroke="#00d4ff" strokeWidth="2"/>
                      <path d="M2 10h20" stroke="#00d4ff" strokeWidth="2"/>
                      <circle cx="7" cy="15" r="1" fill="#00d4ff"/>
                      <circle cx="12" cy="15" r="1" fill="#00d4ff"/>
                      <circle cx="17" cy="15" r="1" fill="#00d4ff"/>
                    </svg>
                  </div>
                  <div className="method-info">
                    <h3>UPI Payment</h3>
                    <p>Pay via UPI (India)</p>
                    <span className="method-badge instant">Instant</span>
                  </div>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              <div className="payment-summary">
                <div className="summary-item">
                  <span>Order ID:</span>
                  <strong>{orderId}</strong>
                </div>
                <div className="summary-item">
                  <span>Amount to Pay:</span>
                  <strong>{calculateTotal()} USDT</strong>
                </div>
                <div className="summary-item" style={{borderTop: '1px solid rgba(0, 212, 255, 0.2)', paddingTop: '12px', marginTop: '8px'}}>
                  <span>UPI Amount (INR):</span>
                  <strong style={{color: '#ffd700'}}>
                    ₹{(parseFloat(calculateTotal()) * exchangeRate).toFixed(2)}
                  </strong>
                </div>
                {rateLoading ? (
                  <div style={{textAlign: 'center', color: '#ffd700', fontSize: '13px', marginTop: '8px'}}>
                    🔄 Fetching live exchange rate...
                  </div>
                ) : (
                  <div style={{textAlign: 'center', color: '#00d4ff', fontSize: '13px', marginTop: '8px'}}>
                    💱 Live Rate: ₹{exchangeRate.toFixed(2)}/USDT
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Payment Details or UPI Instructions */}
        {step === 3 && selectedPaymentMethod === 'UPI' && (
          <div className="upi-container">
            <div className="upi-card">
              <div className="upi-header">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="5" width="20" height="14" rx="2" stroke="#00d4ff" strokeWidth="2"/>
                  <path d="M2 10h20" stroke="#00d4ff" strokeWidth="2"/>
                  <circle cx="7" cy="15" r="1" fill="#00d4ff"/>
                  <circle cx="12" cy="15" r="1" fill="#00d4ff"/>
                  <circle cx="17" cy="15" r="1" fill="#00d4ff"/>
                </svg>
                <h2>UPI Payment Selected</h2>
              </div>

              <div className="upi-info">
                <div className="info-box">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="#ffd700" strokeWidth="2"/>
                    <path d="M12 6v12M9 9h4.5a1.5 1.5 0 010 3H9M9 15h4.5a1.5 1.5 0 000-3H9" stroke="#ffd700" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <div>
                    <strong>Amount to Pay:</strong>
                    <p className="amount-large">₹{(parseFloat(calculateTotal()) * exchangeRate).toFixed(2)}</p>
                    <small>({calculateTotal()} USDT)</small>
                    {rateLoading ? (
                      <small style={{display: 'block', color: '#ffd700', marginTop: '4px'}}>
                        Updating rate...
                      </small>
                    ) : (
                      <small style={{display: 'block', color: '#00d4ff', marginTop: '4px'}}>
                        Live Rate: ₹{exchangeRate.toFixed(2)}/USDT
                      </small>
                    )}
                  </div>
                </div>

                <div className="info-box">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <div>
                    <strong>Order ID:</strong>
                    <p>{orderId}</p>
                  </div>
                </div>
              </div>

              <div className="upi-instructions">
                <h3>Complete Your Order</h3>
                <p>Choose your preferred method to contact the seller and complete UPI payment:</p>
                
                <div className="contact-options-grid">
                  <a 
                    href={`https://t.me/codersaurav?text=${encodeURIComponent(
                      `🛒 NEW UPI ORDER REQUEST\n\n` +
                      `📋 Order ID: ${orderId}\n` +
                      `💰 Amount: ₹${(parseFloat(calculateTotal()) * exchangeRate).toFixed(2)} (${calculateTotal()} USDT)\n` +
                      `📊 Exchange Rate: ₹${exchangeRate.toFixed(2)}/USDT\n` +
                      `⚡ Flash USDT: ${formData.flashAmount} FUSDT\n\n` +
                      `👤 Customer Details:\n` +
                      `Name: ${formData.name}\n` +
                      `Email: ${formData.email}\n` +
                      `${formData.telegramContact ? `Telegram: ${formData.telegramContact}\n` : ''}` +
                      `Wallet: ${formData.walletAddress}\n\n` +
                      `💳 Payment Method: UPI\n\n` +
                      `I want to complete my order via UPI payment. Please provide UPI details.`
                    )}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="contact-seller-btn telegram-btn"
                    onClick={async (e) => {
                      // Send notification to bot
                      try {
                        await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/notify-upi-contact`, {
                          orderId,
                          amount: calculateTotal(),
                          amountINR: (parseFloat(calculateTotal()) * exchangeRate).toFixed(2),
                          exchangeRate: exchangeRate.toFixed(2),
                          flashAmount: formData.flashAmount,
                          name: formData.name,
                          email: formData.email,
                          telegramContact: formData.telegramContact,
                          walletAddress: formData.walletAddress,
                          contactMethod: 'telegram'
                        });
                      } catch (error) {
                        console.error('Failed to send notification:', error);
                      }
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" fill="currentColor"/>
                    </svg>
                    Talk to Seller
                  </a>

                  <a 
                    href={`https://wa.me/919920015661?text=${encodeURIComponent(
                      `🛒 NEW UPI ORDER REQUEST\n\n` +
                      `📋 Order ID: ${orderId}\n` +
                      `💰 Amount: ₹${(parseFloat(calculateTotal()) * exchangeRate).toFixed(2)} (${calculateTotal()} USDT)\n` +
                      `📊 Exchange Rate: ₹${exchangeRate.toFixed(2)}/USDT\n` +
                      `⚡ Flash USDT: ${formData.flashAmount} FUSDT\n\n` +
                      `👤 Customer Details:\n` +
                      `Name: ${formData.name}\n` +
                      `Email: ${formData.email}\n` +
                      `${formData.telegramContact ? `Contact: ${formData.telegramContact}\n` : ''}` +
                      `Wallet: ${formData.walletAddress}\n\n` +
                      `💳 Payment Method: UPI\n\n` +
                      `I want to complete my order via UPI payment. Please provide UPI details.`
                    )}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="contact-seller-btn whatsapp-btn"
                    onClick={async (e) => {
                      // Send notification to bot
                      try {
                        await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/notify-upi-contact`, {
                          orderId,
                          amount: calculateTotal(),
                          amountINR: (parseFloat(calculateTotal()) * exchangeRate).toFixed(2),
                          exchangeRate: exchangeRate.toFixed(2),
                          flashAmount: formData.flashAmount,
                          name: formData.name,
                          email: formData.email,
                          telegramContact: formData.telegramContact,
                          walletAddress: formData.walletAddress,
                          contactMethod: 'whatsapp'
                        });
                      } catch (error) {
                        console.error('Failed to send notification:', error);
                      }
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="currentColor"/>
                    </svg>
                    Talk to Seller
                  </a>
                </div>

                <div className="instruction-steps">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <p>Click Telegram or WhatsApp button above</p>
                  </div>
                  <div className="step-item">
                    <span className="step-number">2</span>
                    <p>Your order details will be automatically filled in the message</p>
                  </div>
                  <div className="step-item">
                    <span className="step-number">3</span>
                    <p>Send the message and wait for seller's UPI payment details</p>
                  </div>
                  <div className="step-item">
                    <span className="step-number">4</span>
                    <p>Complete UPI payment and share screenshot with seller</p>
                  </div>
                  <div className="step-item">
                    <span className="step-number">5</span>
                    <p>Receive Flash USDT in your wallet within 10-15 minutes</p>
                  </div>
                </div>
              </div>

              <button onClick={() => {
                setStep(2);
                setSelectedPaymentMethod(null);
              }} className="back-to-methods-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Crypto Payment Details */}
        {step === 3 && selectedPaymentMethod !== 'UPI' && (
          <div className="payment-container">
            <div className="payment-card">
              <h2>Complete Payment</h2>

              <div className="timer-section">
                <div className="timer-display">
                  <span className="timer-icon"><TimerIcon size={32} color="#8a2be2" /></span>
                  <span className="timer-text">{formatTime(timeLeft)}</span>
                </div>
                <div className="timer-bar">
                  <div 
                    className="timer-progress" 
                    style={{ width: `${(timeLeft / 1800) * 100}%` }}
                  ></div>
                </div>
                <p className="timer-warning">Complete payment within 30 minutes</p>
              </div>

              <div className="payment-details">
                <div className="qr-section">
                  <div className="qr-code-container">
                    <img 
                      src={paymentData.qrCode} 
                      alt="Payment QR Code" 
                      className="qr-code-image"
                    />
                    <p className="qr-text">Scan to Pay</p>
                  </div>
                  
                  <div className="payment-info-compact">
                    <div className="info-row">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="info-label">Order ID:</span>
                      <span className="info-value">{orderId}</span>
                    </div>
                    
                    <div className="info-row amount-row">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="#ffd700" strokeWidth="2"/>
                        <path d="M12 6v12M9 9h4.5a1.5 1.5 0 010 3H9M9 15h4.5a1.5 1.5 0 000-3H9" stroke="#ffd700" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span className="info-label">Amount:</span>
                      <span className="info-value amount-highlight">{paymentData.amountToPay} USDT</span>
                    </div>
                  </div>
                  
                  <div className="network-warning">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{flexShrink: 0}}>
                      <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Send funds only via <strong>{selectedCrypto.name}</strong> network, otherwise funds will be lost</span>
                  </div>
                </div>

                <div className="wallet-section">
                  <label>Payment Wallet Address:</label>
                  <div className="wallet-address">
                    <input 
                      type="text" 
                      value={paymentData.paymentWallet} 
                      readOnly 
                    />
                    <button 
                      onClick={() => navigator.clipboard.writeText(paymentData.paymentWallet)}
                      className="copy-btn"
                    >
                      <CopyIcon size={16} /> Copy
                    </button>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="payment-form">
                <div className="form-group">
                  <label>Transaction Hash *</label>
                  <input
                    type="text"
                    name="transactionHash"
                    value={formData.transactionHash}
                    onChange={handleChange}
                    placeholder="Enter transaction hash"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Upload Payment Screenshot *</label>
                  <div 
                    className="file-upload-area"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => document.getElementById('screenshot-input').click()}
                  >
                    {!screenshotPreview ? (
                      <>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p className="upload-text">Drop your screenshot here or <span>click to browse</span></p>
                        <p className="upload-hint">PNG, JPG up to 10MB</p>
                      </>
                    ) : (
                      <div className="screenshot-preview">
                        <img src={screenshotPreview} alt="Screenshot preview" />
                        <div className="preview-overlay">
                          <p className="file-name-preview">✓ {formData.screenshot.name}</p>
                          <button 
                            type="button" 
                            className="remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeScreenshot();
                            }}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M18 6L6 18M6 6l12 12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    id="screenshot-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    required
                  />
                </div>

                {message.text && (
                  <div className={`message ${message.type}`}>
                    {message.text}
                  </div>
                )}

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="button-spinner"></span>
                      Processing Order...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Place Order
                    </>
                  )}
                </button>

                <button 
                  type="button"
                  onClick={() => {
                    setStep(2);
                    setSelectedPaymentMethod(null);
                  }} 
                  className="back-to-methods-btn"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </form>
            </div>
          </div>
        )}

        {/* STEP 4: Processing */}
        {step === 4 && processing && (
          <div className="processing-container">
            <div className="processing-card">
              <div className="processing-spinner">
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
              </div>
              
              <h2>Processing Your Order</h2>
              <p className="processing-subtitle">Please wait while we verify your payment...</p>
              
              <div className="processing-steps">
                <div className={`processing-step ${processingProgress >= 20 ? 'active' : ''}`}>
                  <div className="step-check">
                    {processingProgress >= 20 ? <CheckIcon size={16} color="#00d4ff" /> : '1'}
                  </div>
                  <span>Verifying Payment</span>
                </div>
                <div className={`processing-step ${processingProgress >= 50 ? 'active' : ''}`}>
                  <div className="step-check">
                    {processingProgress >= 50 ? <CheckIcon size={16} color="#00d4ff" /> : '2'}
                  </div>
                  <span>Confirming Transaction</span>
                </div>
                <div className={`processing-step ${processingProgress >= 80 ? 'active' : ''}`}>
                  <div className="step-check">
                    {processingProgress >= 80 ? <CheckIcon size={16} color="#00d4ff" /> : '3'}
                  </div>
                  <span>Processing Order</span>
                </div>
              </div>

              <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${processingProgress}%` }}></div>
              </div>
              <p className="progress-text">{processingProgress}% Complete</p>
              
              <p className="processing-note">
                <strong>Order ID:</strong> {orderId}
              </p>
            </div>
          </div>
        )}

        {/* STEP 5: Success */}
        {step === 5 && (
          <div className="success-container">
            <div className="success-card">
              <div className="success-icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="40" r="38" fill="url(#successGradient)" opacity="0.15"/>
                  <circle cx="40" cy="40" r="30" fill="url(#successGradient)"/>
                  <path d="M25 40l10 10 20-20" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="successGradient" x1="0" y1="0" x2="80" y2="80">
                      <stop offset="0%" stopColor="#00d4ff"/>
                      <stop offset="100%" stopColor="#0099cc"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <h2>Order Placed Successfully!</h2>
              <p className="order-id-large">Order ID: {orderId}</p>
              
              <div className="processing-notice">
                <div className="notice-icon">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <circle cx="20" cy="20" r="18" stroke="#00d4ff" strokeWidth="2.5"/>
                    <path d="M20 12v10M20 28h.02" stroke="#00d4ff" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="notice-content">
                  <h3>Please Wait - Your Flash USDT is Processing</h3>
                  <p>
                    Our team is currently verifying your transaction ID and payment screenshot. 
                    This verification process may take some time to ensure security and accuracy.
                  </p>
                </div>
              </div>
              
              <div className="success-info">
                <div className="info-card">
                  <div className="info-icon">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <circle cx="20" cy="20" r="18" stroke="#00d4ff" strokeWidth="2.5"/>
                      <path d="M20 10v10l6 6" stroke="#00d4ff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="info-text">
                    <strong>Quick Delivery</strong>
                    <p>Your Flash USDT will appear in your wallet within <span className="highlight-text">10-15 minutes</span> after verification</p>
                  </div>
                </div>
                
                <div className="info-card">
                  <div className="info-icon">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <circle cx="20" cy="20" r="18" stroke="#10b981" strokeWidth="2.5"/>
                      <circle cx="20" cy="20" r="8" stroke="#10b981" strokeWidth="2.5"/>
                      <path d="M20 2v6M20 32v6M2 20h6M32 20h6" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="info-text">
                    <strong>Verification in Progress</strong>
                    <p>Our team is verifying your payment details for security purposes</p>
                  </div>
                </div>
                
                <div className="info-card support-card">
                  <div className="info-icon">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <circle cx="20" cy="20" r="18" stroke="#ffd700" strokeWidth="2.5"/>
                      <path d="M20 14v8M20 28h.02" stroke="#ffd700" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="info-text">
                    <strong>Not Received Yet?</strong>
                    <p>If you don't receive Flash USDT within 15 minutes, please contact our support team</p>
                    <a 
                      href="https://t.me/flashsellersupportbot" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="contact-support-link"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" fill="#00d4ff"/>
                      </svg>
                      Contact Support
                    </a>
                  </div>
                </div>

                <div className="wallet-info">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{marginRight: '10px', verticalAlign: 'middle'}}>
                    <rect x="2" y="6" width="20" height="14" rx="2" stroke="#00d4ff" strokeWidth="2"/>
                    <circle cx="17" cy="13" r="2" stroke="#00d4ff" strokeWidth="2"/>
                    <path d="M6 6V4a2 2 0 012-2h8a2 2 0 012 2v2" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <strong>Receiving Wallet:</strong>
                  <p>{formData.walletAddress}</p>
                </div>
                <div className="amount-info">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{marginRight: '10px', verticalAlign: 'middle'}}>
                    <circle cx="12" cy="12" r="10" stroke="#ffd700" strokeWidth="2"/>
                    <path d="M12 6v12M9 9h4.5a1.5 1.5 0 010 3H9M9 15h4.5a1.5 1.5 0 000-3H9" stroke="#ffd700" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <strong>Flash USDT Amount:</strong>
                  <p>{formData.flashAmount} FUSDT</p>
                </div>
              </div>

              <div className="success-actions">
                <button 
                  onClick={() => {
                    setNewOrderLoading(true);
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  }} 
                  className="new-order-btn"
                  disabled={newOrderLoading}
                >
                  {newOrderLoading ? (
                    <>
                      <span className="button-spinner"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Place New Order
                    </>
                  )}
                </button>
                <button onClick={onBack} className="home-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Back to Home
                </button>
              </div>

              <p className="confirmation-email">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{marginRight: '8px', verticalAlign: 'middle'}}>
                  <rect x="2" y="4" width="20" height="16" rx="2" stroke="#00d4ff" strokeWidth="2"/>
                  <path d="M2 7l10 7 10-7" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                A confirmation email has been sent to {formData.email}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OrderForm;
